import { usersApi } from '../../api/users';
import { makeApiRequest, cancelAllApiRequests } from '../../utils/ApiManager';

/**
 * Registration Manager for handling user registration checks
 */
export class RegistrationManager {
  constructor() {
    // Debounced cache for registration checks to prevent API spam
    this.registrationCheckCache = new Map();
    this.registrationCheckTimeouts = new Map();
    this.activeRegistrationChecks = new Set(); // Track active checks
    this.positiveCacheTtl = 30000; // 30s for registered users
    this.negativeCacheTtl = 5 * 60 * 1000; // 5 min for unregistered/404
  }

  /**
   * Check if user is registered (with caching)
   */
  async checkUserRegistration(address, setIsUserRegistered, setRegistrationChecked, setUsername) {
    const normalizedAddress = address?.toLowerCase();
    
    if (!normalizedAddress) {
      setIsUserRegistered(null);
      setRegistrationChecked(true);
      return;
    }
    
    // Prevent duplicate checks for the same address
    if (this.activeRegistrationChecks.has(normalizedAddress)) {
      return;
    }
    
    // Check cache first (registered: 30s, unregistered: 5 min)
    const cached = this.registrationCheckCache.get(normalizedAddress);
    if (cached) {
      const ageMs = Date.now() - cached.timestamp;
      const ttl = cached.isRegistered ? this.positiveCacheTtl : this.negativeCacheTtl;
      if (ageMs < ttl) {
        setIsUserRegistered(cached.isRegistered);
        setUsername(cached.username || null);
        setRegistrationChecked(true);
        return;
      }
    }
    
    this.activeRegistrationChecks.add(normalizedAddress);
    
    try {
      
      // Create the API call function
      const apiCallFunction = () => usersApi.getByAddress(normalizedAddress);
      
      // Use makeApiRequest with proper parameters: key, apiCall function, priority
      const response = await makeApiRequest(
        `registration_${normalizedAddress}`, 
        apiCallFunction, 
        5 // High priority for registration checks
      );
      
      
      let isRegistered = false;
      let username = null;
      
      if (response && response.success && response.user) {
        isRegistered = true;
        username = response.user.username || response.user.referralCode || null;
      } else {
      }
      
      // Cache the result
      this.registrationCheckCache.set(normalizedAddress, {
        isRegistered,
        username,
        timestamp: Date.now()
      });
      
      // Update state
      setIsUserRegistered(isRegistered);
      setUsername(username);
      setRegistrationChecked(true);
      
    } catch (error) {
      
      // On error, assume not registered but mark as checked
      setIsUserRegistered(false);
      setUsername(null);
      setRegistrationChecked(true);
      
      // Cache the error result to avoid repeated 404 spam
      this.registrationCheckCache.set(normalizedAddress, {
        isRegistered: false,
        username: null,
        timestamp: Date.now()
      });
    } finally {
      this.activeRegistrationChecks.delete(normalizedAddress);
    }
  }

  /**
   * Create debounced version of checkUserRegistration
   */
  createDebouncedCheck(setIsUserRegistered, setRegistrationChecked, setUsername) {
    return (address) => {
      const normalizedAddress = address?.toLowerCase();
      
      if (!normalizedAddress) {
        return;
      }
      
      
      // Clear existing timeout for this address
      if (this.registrationCheckTimeouts.has(normalizedAddress)) {
        clearTimeout(this.registrationCheckTimeouts.get(normalizedAddress));
      }
      
      // Set new timeout
      const timeoutId = setTimeout(() => {
        this.checkUserRegistration(normalizedAddress, setIsUserRegistered, setRegistrationChecked, setUsername);
        this.registrationCheckTimeouts.delete(normalizedAddress);
      }, 1000); // 1 second debounce
      
      this.registrationCheckTimeouts.set(normalizedAddress, timeoutId);
    };
  }

  /**
   * Refresh user data for a specific address
   */
  async refreshUserData(address, setIsUserRegistered, setRegistrationChecked, setUsername) {
    const normalizedAddress = address?.toLowerCase();
    
    if (!normalizedAddress) {
      return;
    }
    
    // Clear cache for this address to force fresh check
    this.registrationCheckCache.delete(normalizedAddress);
    
    // Clear any pending timeout
    if (this.registrationCheckTimeouts.has(normalizedAddress)) {
      clearTimeout(this.registrationCheckTimeouts.get(normalizedAddress));
      this.registrationCheckTimeouts.delete(normalizedAddress);
    }
    
    await this.checkUserRegistration(normalizedAddress, setIsUserRegistered, setRegistrationChecked, setUsername);
  }

  /**
   * Clear all caches and timeouts
   */
  cleanup() {
    // Cancel all pending API requests
    cancelAllApiRequests();
    
    // Clear all timeouts
    this.registrationCheckTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.registrationCheckTimeouts.clear();
    
    // Clear caches
    this.registrationCheckCache.clear();
    this.activeRegistrationChecks.clear();
    
  }

  /**
   * Reset registration state
   */
  resetRegistrationState(setIsUserRegistered, setRegistrationChecked, setUsername) {
    setIsUserRegistered(null);
    setRegistrationChecked(false);
    setUsername(null);
  }
}

// Create a singleton instance
export const registrationManager = new RegistrationManager(); 